import { useState, useEffect } from 'react';

/**
 * Fetches policy text from a URL (e.g. Azure Blob Storage).
 * Set REACT_APP_TERMS_TEXT_URL / REACT_APP_PRIVACY_TEXT_URL to your blob URLs.
 * If unset, falls back to /policies/terms.txt and /policies/privacy.txt (local or same-origin).
 */
export function usePolicyText(url: string | undefined) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const effectiveUrl = url?.trim() || '';
    if (!effectiveUrl) {
      setError('Policy URL is not configured.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(effectiveUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load policy (${res.status})`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) {
          setContent(text);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load policy text.');
          setContent(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { content, loading, error };
}

/** Resolve policy text URL: env override or fallback to public path */
export function getPolicyTextUrl(envUrl: string | undefined, fallbackPath: string): string {
  if (envUrl?.trim()) return envUrl.trim();
  return fallbackPath;
}

/** Resolve policy PDF URL: env override (e.g. Azure Blob) or fallback to local path */
export function getPolicyPdfUrl(envUrl: string | undefined, fallbackPath: string): string {
  if (envUrl?.trim()) return envUrl.trim();
  return fallbackPath;
}
